import { useState, useMemo } from 'react';
import CountryCode from 'react-phone-input-2';
import { Input, Select, TextArea, Button, Checkbox, Tooltip, Toggle, DatePicker } from 'common/components';
import { InfoIcon, PencilIcon, TrashIcon } from 'lucide-react';

import { WorkflowConfigFormData } from '../../AgenticWorkflowBuilder';
import {
    GENERIC_DISPOSITION_KEYS,
    DISPOSITION_LABEL_MAX_LENGTH,
    validateDispositionLabel,
} from '../../dispositionLabels';
import { Controller, useFormContext } from 'react-hook-form';
import { WorkflowTagsType } from 'pages/WorkflowBuilder/WorkflowBuilder.types';
import {
    ALERT_THRESHOLD_FALLBACK,
    ALERT_THRESHOLD_MAX,
    ALERT_THRESHOLD_MIN,
    MAX_CALLS_PER_DAY_MAX,
    MAX_CALLS_PER_DAY_MIN,
    PRIORITIZATION_MODE_OPTIONS,
    TIMEZONE_REQUIREMENT_MESSAGE,
    UNSET_BEHAVIOUR_MESSAGE,
    hasOperatingHoursTimezone,
    validateAlertThreshold,
    validateBusinessDayEnd,
    validateMaxCallsPerDay,
} from 'pages/WorkflowBuilder/queueCadenceUtils';
import {
    SMS_PACING_DEFAULT,
    SMS_PACING_MAX,
    SMS_PACING_MIN,
    validateSmsPacingMaxPerMinute,
} from 'pages/WorkflowBuilder/smsPacingUtils';
import {
    DAILY_CALL_CAP_DEFAULT,
    DAILY_CALL_CAP_MAX,
    DAILY_CALL_CAP_MIN,
    validateDailyCallCap,
} from 'pages/WorkflowBuilder/numberLimitsUtils';
import { Accordion } from 'components/Accordion';
import { GenericTagsModal } from '../WorkflowGenericTagTabs/GenericTagsModal';
import { PartyStatusModal } from '../PartyStatus';
import { TransferAudioSection } from '../TransferAudio';
import { STANDARD_TIMEZONE_OPTIONS, STANDARD_TZ_VALUES } from 'common/constants';
import { formatDisposition } from 'pages/AgenticWorkflowDetail/utils';
import { useGetPrompts } from 'services/hooks/queries';
import { EffectiveInboundPrompt } from 'services/api/WorkflowServiceAPI';
import {
    AUTO_INBOUND_PROMPT_LABEL,
    AutoInboundPrompt,
    NO_INBOUND_PROMPT_WARNING,
} from 'pages/WorkflowBuilder/inboundPrompt';
import { AREA_CODE_RULES } from 'pages/WorkflowBuilder/areaCode';

const workflowTagsTypeOptions = [
    { value: WorkflowTagsType.PREDEFINED, label: 'Predefined' },
    { value: WorkflowTagsType.GENERIC, label: 'Generic' },
];

export const WorkflowConfigs = ({
    isEdit,
    canEditStatusModel = !isEdit,
    canSetAreaCode = !isEdit,
    canEditTransitions = false,
    workflowId,
    autoInboundPrompt = null,
    effectiveInboundPrompt,
    canEditNumberLimits = false,
}: {
    isEdit: boolean;
    /** Party statuses are settable when creating or duplicating, not when editing. */
    canEditStatusModel?: boolean;
    /** The area code is collected when creating or duplicating, not when editing. */
    canSetAreaCode?: boolean;
    /** Transitions alone stay editable on a saved workflow. */
    canEditTransitions?: boolean;
    workflowId?: string;
    /** What Auto resolves to on the current canvas (see getAutoInboundPrompt). */
    autoInboundPrompt?: AutoInboundPrompt | null;
    /** What the backend currently uses for inbound. Only passed when editing a saved workflow. */
    effectiveInboundPrompt?: EffectiveInboundPrompt;
    /** Super admins only; when false the Phone lines section is not rendered or registered. */
    canEditNumberLimits?: boolean;
}) => {
    const [isGenericModalOpen, setIsGenericModalOpen] = useState(false);
    const [isPartyStatusModalOpen, setIsPartyStatusModalOpen] = useState(false);
    const [dispositionNamesOpen, setDispositionNamesOpen] = useState(false);
    const {
        register,
        control,
        setValue,
        resetField,
        getValues,
        watch,
        trigger,
        formState: { errors },
    } = useFormContext<WorkflowConfigFormData>();
    const isGenericConfigured = watch('isGenericConfigured');
    const operatingHoursEnabled = watch('operatingHoursEnabled');
    const currentTimezone = watch('operatingHoursTimezone');
    const inboundPromptId = watch('inboundPromptId');
    const { prompts, isLoadingPrompts } = useGetPrompts();

    // Every tenant prompt, no filtering. A stored id that is no longer in the
    // list (deleted prompt) still gets an option so the select shows the truth;
    // the backend falls back to Auto for it.
    const inboundPromptOptions = useMemo(() => {
        const opts = [
            { value: '', label: AUTO_INBOUND_PROMPT_LABEL },
            ...(prompts?.map((prompt) => ({ value: prompt.id, label: prompt.prompt_name })) ?? []),
        ];
        if (inboundPromptId && prompts && !prompts.some((prompt) => prompt.id === inboundPromptId)) {
            opts.push({ value: inboundPromptId, label: 'Unavailable prompt' });
        }
        return opts;
    }, [prompts, inboundPromptId]);

    const autoInboundPromptName = autoInboundPrompt
        ? autoInboundPrompt.promptName ||
          prompts?.find((prompt) => prompt.id === autoInboundPrompt.promptId)?.prompt_name ||
          autoInboundPrompt.promptId
        : '';
    // The daily cap, same-day enforcement and business day end are rejected by
    // the backend without an operating hours timezone to anchor a day against.
    const dayBoundaryLocked = !hasOperatingHoursTimezone(currentTimezone);

    const timezoneOptions = useMemo(() => {
        const opts = [...STANDARD_TIMEZONE_OPTIONS];
        if (currentTimezone && !STANDARD_TZ_VALUES.has(currentTimezone) && currentTimezone !== 'OTHER') {
            opts.push({ value: currentTimezone, label: currentTimezone });
        }
        opts.push({ value: 'OTHER', label: 'Other' });
        return opts;
    }, [currentTimezone]);

    const handleResetGeneric = () => {
        setValue('isGenericConfigured', false);
        setValue('workflowTagsType', WorkflowTagsType.PREDEFINED);
        resetField('genericTags');
        resetField('genericPrompts');
    };

    const dayBoundaryControls = (
        <div>
            <div className='mt-md flex gap-sm'>
                <div style={{ flex: 1 }}>
                    <Input
                        id='max-calls-per-day'
                        label='Max calls per party per day'
                        type='number'
                        placeholder='No cap'
                        {...register('maxCallsPerDay', {
                            validate: (value) => dayBoundaryLocked || validateMaxCallsPerDay(value),
                        })}
                        min={MAX_CALLS_PER_DAY_MIN}
                        max={MAX_CALLS_PER_DAY_MAX}
                        disabled={dayBoundaryLocked}
                        error={!!errors.maxCallsPerDay}
                        errorText={errors.maxCallsPerDay?.message}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <Input
                        id='business-day-end'
                        label='Business day end'
                        type='time'
                        {...register('businessDayEnd', {
                            validate: (value) =>
                                dayBoundaryLocked ||
                                validateBusinessDayEnd(value, {
                                    sameDayEnforcement: getValues('sameDayEnforcement'),
                                    operatingHoursEnabled: getValues('operatingHoursEnabled'),
                                    operatingHoursStartTime: getValues('operatingHoursStartTime'),
                                }),
                        })}
                        disabled={dayBoundaryLocked}
                        error={!!errors.businessDayEnd}
                        errorText={errors.businessDayEnd?.message}
                    />
                </div>
            </div>

            <div className='mt-md flex items-center gap-sm'>
                <Controller
                    control={control}
                    name='sameDayEnforcement'
                    render={({ field }) => (
                        <Toggle
                            // null is "never configured", which renders as off but,
                            // unlike a stored false, is left out of the save payload.
                            checked={field.value ?? false}
                            onChange={(e) => {
                                field.onChange(e.target.checked);
                                // The business day end is only in conflict with the
                                // operating hours start while enforcement is on.
                                trigger('businessDayEnd');
                            }}
                            onBlur={field.onBlur}
                            name={field.name}
                            disabled={dayBoundaryLocked}
                            aria-label='Toggle same-day enforcement'
                        />
                    )}
                />
                <span className='text-sm text-gray-700'>Same-day enforcement</span>
                <Tooltip
                    content='Calls still unexecuted at the business day end roll to the next occurrence of their cadence day instead of dialing after hours.'
                    position='top'
                >
                    <span className='flex items-center'>
                        <InfoIcon size={14} color='var(--primary-purple)' />
                    </span>
                </Tooltip>
            </div>
        </div>
    );

    return (
        <div>
            <h3>Workflow Configs</h3>
            <div className='mt-xl'>
                <Input
                    label='Workflow Name'
                    type='text'
                    error={!!errors.flowName}
                    errorText={errors.flowName?.message}
                    {...register('flowName', { required: 'Please enter a workflow name' })}
                    placeholder='Enter Workflow Name...'
                />
            </div>
            <div className='mt-lg'>
                <TextArea
                    label='Workflow Description'
                    error={!!errors.flowDescription}
                    errorText={errors.flowDescription?.message}
                    placeholder='Enter Workflow Description...'
                    rows={2}
                    resize='none'
                    {...register('flowDescription', { required: 'Please enter a workflow description' })}
                />
            </div>

            {/* Only shown on create/duplicate — the backend auto-provisions one
                phone number per workflow at creation and ignores the field on
                update. Same 100-999 validation as the buy-numbers admin form. */}
            {canSetAreaCode && (
                <div className='mt-lg'>
                    <Input
                        label='Area Code *'
                        type='text'
                        placeholder='e.g., 415'
                        maxLength={3}
                        error={!!errors.areaCode}
                        errorText={errors.areaCode?.message}
                        {...register('areaCode', AREA_CODE_RULES)}
                    />
                </div>
            )}

            <div className='mt-lg flex items-center gap-sm'>
                <Controller
                    control={control}
                    name='isHumanTransfer'
                    render={({ field }) => (
                        <Checkbox
                            label='Enable human handoff'
                            checked={field.value}
                            onChange={(e) => {
                                field.onChange(e.target.checked);
                                if (e.target.checked) setValue('allowCallsWithoutOnlineAgents', false);
                            }}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                        />
                    )}
                />
                <Tooltip
                    content='When enabled, calls are transferred to a human agent after successful disposition (i.e. once the AI has completed the conversation).'
                    position='top'
                >
                    <span className='flex items-center'>
                        <InfoIcon size={16} color='var(--primary-purple)' />
                    </span>
                </Tooltip>
            </div>

            <div className='mt-lg flex items-center gap-sm'>
                <Controller
                    control={control}
                    name='allowCallsWithoutOnlineAgents'
                    render={({ field }) => (
                        <Checkbox
                            label='Agent-less queue'
                            checked={!!field.value}
                            onChange={(e) => {
                                field.onChange(e.target.checked);
                                if (e.target.checked) setValue('isHumanTransfer', false);
                            }}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                        />
                    )}
                />
                <Tooltip
                    content='When enabled, AI calls will be dispatched even when no human agents are online for this workflow. Not compatible with human handoff.'
                    position='top'
                >
                    <span className='flex items-center'>
                        <InfoIcon size={16} color='var(--primary-purple)' />
                    </span>
                </Tooltip>
            </div>

            <div className='mt-lg flex items-center gap-sm'>
                <Controller
                    control={control}
                    name='crmCreateContactOnInbound'
                    render={({ field }) => (
                        <Checkbox
                            label='Send inbound contacts to LawRuler'
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                        />
                    )}
                />
                <Tooltip
                    content='When enabled, contacts from inbound calls are created in LawRuler CRM.'
                    position='top'
                >
                    <span className='flex items-center'>
                        <InfoIcon size={16} color='var(--primary-purple)' />
                    </span>
                </Tooltip>
            </div>

            <div className='mt-lg'>
                <div className='flex items-end gap-sm'>
                    <div style={{ flex: 1 }}>
                        <Controller
                            control={control}
                            name='inboundPromptId'
                            render={({ field }) => (
                                <Select
                                    label='Inbound prompt'
                                    options={inboundPromptOptions}
                                    value={field.value ?? ''}
                                    onChange={(event) => field.onChange(event.target.value)}
                                    disabled={isLoadingPrompts}
                                />
                            )}
                        />
                    </div>
                    <Tooltip
                        content="Prompt the AI uses when someone calls this workflow's number. Auto uses the prompt on the first call node in the flow."
                        position='top'
                    >
                        <span className='flex items-center' style={{ paddingBottom: '10px' }}>
                            <InfoIcon size={16} color='var(--primary-purple)' />
                        </span>
                    </Tooltip>
                </div>
                {!inboundPromptId && (
                    <>
                        {autoInboundPrompt ? (
                            <p
                                data-testid='inbound-prompt-auto'
                                style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--gray-700)' }}
                            >
                                Using: {autoInboundPromptName}
                            </p>
                        ) : (
                            <p
                                data-testid='inbound-prompt-warning'
                                style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--warning-color)' }}
                            >
                                {NO_INBOUND_PROMPT_WARNING}
                            </p>
                        )}
                        {isEdit && effectiveInboundPrompt && (
                            <p
                                data-testid='inbound-prompt-live'
                                style={{
                                    margin: '4px 0 0',
                                    fontSize: '0.8rem',
                                    color:
                                        effectiveInboundPrompt.source === 'none'
                                            ? 'var(--warning-color)'
                                            : 'var(--gray-700)',
                                }}
                            >
                                Currently:{' '}
                                {effectiveInboundPrompt.source === 'none'
                                    ? 'none'
                                    : effectiveInboundPrompt.promptName || effectiveInboundPrompt.promptId}
                            </p>
                        )}
                    </>
                )}
            </div>

            <div className='mt-lg flex items-end gap-sm'>
                <Controller
                    control={control}
                    name='workflowTagsType'
                    render={({ field }) => (
                        <Select
                            label='Workflow tags type'
                            options={workflowTagsTypeOptions}
                            disabled={isEdit}
                            value={field.value}
                            onChange={(event) => {
                                field.onChange(event.target.value);
                                if (event.target.value === WorkflowTagsType.GENERIC) {
                                    setIsGenericModalOpen(true);
                                }
                            }}
                        />
                    )}
                />

                {isGenericConfigured && (
                    <div className='flex items-center gap-sm mt-md'>
                        <Button
                            variant='secondary'
                            icon={PencilIcon}
                            iconOnly
                            onClick={() => setIsGenericModalOpen(true)}
                        />
                        {!isEdit && <Button variant='danger' icon={TrashIcon} iconOnly onClick={handleResetGeneric} />}
                    </div>
                )}
            </div>

            {/*
              Party statuses can only be set while creating: parties hold their
              stage as a plain string, so renaming or removing a status would
              strand everyone already on it. Transitions only apply to calls that
              finish after the change, so an edit opens them on their own.
            */}
            {(canEditStatusModel || canEditTransitions) && (
                <div className='mt-lg'>
                    <Button variant='secondary' onClick={() => setIsPartyStatusModalOpen(true)}>
                        {canEditStatusModel ? 'Party Status' : 'Status Transitions'}
                    </Button>
                </div>
            )}

            {isGenericConfigured && (
                <div className='mt-lg'>
                    <Accordion
                        isOpen={dispositionNamesOpen}
                        onToggle={() => setDispositionNamesOpen((open) => !open)}
                        header='Disposition names (optional)'
                    >
                        {GENERIC_DISPOSITION_KEYS.map((key) => (
                            <div className='mt-md' key={key}>
                                <Input
                                    id={`disposition-label-${key}`}
                                    label={`${formatDisposition(key)} name`}
                                    placeholder={formatDisposition(key)}
                                    maxLength={DISPOSITION_LABEL_MAX_LENGTH}
                                    error={!!errors.dispositionLabels?.[key]}
                                    errorText={errors.dispositionLabels?.[key]?.message}
                                    {...register(`dispositionLabels.${key}`, {
                                        validate: (value) =>
                                            validateDispositionLabel(value, key, getValues('dispositionLabels')),
                                    })}
                                />
                            </div>
                        ))}
                    </Accordion>
                </div>
            )}

            {/* Upload is a separate call from the config save and needs a saved workflow to attach to. */}
            {workflowId && (
                <div className='mt-lg'>
                    <TransferAudioSection workflowId={workflowId} />
                </div>
            )}

            <div className='mt-lg'>
                <Controller
                    control={control}
                    name='workflowStart'
                    render={({ field }) => (
                        <DatePicker
                            label='Start Date'
                            selected={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            dateFormat='yyyy-MM-dd'
                            placeholderText='Select start date'
                            isClearable
                        />
                    )}
                />
                <div className='mt-lg'>
                    <Controller
                        control={control}
                        name='workflowEnd'
                        render={({ field }) => (
                            <DatePicker
                                label='End Date'
                                selected={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                ref={field.ref}
                                dateFormat='yyyy-MM-dd'
                                placeholderText='Select end date'
                                isClearable
                            />
                        )}
                    />
                </div>
            </div>
            <div className='mt-lg' style={{ display: 'none' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Country</label>
                <Controller
                    control={control}
                    name='countryCode'
                    render={({ field }) => (
                        <CountryCode
                            country={field.value}
                            value={field.value}
                            onChange={(value, country: any) => {
                                field.onChange(country.iso2);
                                setValue('dialCode', country.dialCode);
                            }}
                            inputStyle={{ width: '100%' }}
                            containerStyle={{ marginBottom: '10px' }}
                            inputProps={{
                                readOnly: true,
                                disabled: true,
                                placeholder: 'Select country',
                            }}
                            buttonStyle={{ background: '#f5f5f5' }}
                            dropdownStyle={{ width: '300px' }}
                            enableSearch={true}
                            disableSearchIcon={false}
                            searchPlaceholder='Search countries'
                        />
                    )}
                />
            </div>
            <div className='mt-lg' style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.9rem', fontWeight: 500 }}>
                    Operating Hours
                </label>
                <Controller
                    control={control}
                    name='operatingHoursEnabled'
                    render={({ field }) => (
                        <div className='flex items-center gap-sm'>
                            <Toggle
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                aria-label='Toggle 24 hour operating hours'
                            />
                            <span className='text-sm text-gray-700'>24 Hours</span>
                        </div>
                    )}
                />
                {!operatingHoursEnabled && (
                    <div className='mt-md'>
                        <div className='flex gap-sm'>
                            <div style={{ flex: 1 }}>
                                <Input
                                    label='Start Time'
                                    type='time'
                                    {...register('operatingHoursStartTime', {
                                        required: 'Start time is required',
                                    })}
                                    error={!!errors.operatingHoursStartTime}
                                    errorText={errors.operatingHoursStartTime?.message}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Input
                                    label='End Time'
                                    type='time'
                                    {...register('operatingHoursEndTime', {
                                        required: 'End time is required',
                                        validate: (end) => {
                                            const start = getValues('operatingHoursStartTime');
                                            return !start || end > start || 'End time must be after start time';
                                        },
                                    })}
                                    error={!!errors.operatingHoursEndTime}
                                    errorText={errors.operatingHoursEndTime?.message}
                                />
                            </div>
                        </div>
                        <div className='mt-md'>
                            <div className='flex items-center gap-xs mb-xs'>
                                <label style={{ fontSize: '0.9rem' }}>Timezone</label>
                                <Tooltip
                                    content='Please reach out to support if you would like a custom timezone'
                                    position='top'
                                >
                                    <span className='flex items-center'>
                                        <InfoIcon size={14} color='var(--primary-purple)' />
                                    </span>
                                </Tooltip>
                            </div>
                            <Controller
                                control={control}
                                name='operatingHoursTimezone'
                                rules={{
                                    validate: (value) =>
                                        getValues('operatingHoursEnabled') ||
                                        value !== 'OTHER' ||
                                        'Please reach out to support for a custom timezone',
                                }}
                                render={({ field }) => (
                                    <Select
                                        options={timezoneOptions}
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        error={!!errors.operatingHoursTimezone}
                                        errorText={errors.operatingHoursTimezone?.message}
                                    />
                                )}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className='mt-lg' style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', fontWeight: 500 }}>
                    Queue &amp; Cadence
                </label>
                <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: 'var(--gray-700)' }}>
                    {UNSET_BEHAVIOUR_MESSAGE}
                </p>

                <div className='flex items-center gap-xs mb-xs'>
                    <label style={{ fontSize: '0.9rem' }}>Prioritization mode</label>
                    <Tooltip
                        content='Decides which pending call goes out first when capacity is limited. Click-to-call and callbacks keep jumping the queue either way.'
                        position='top'
                    >
                        <span className='flex items-center'>
                            <InfoIcon size={14} color='var(--primary-purple)' />
                        </span>
                    </Tooltip>
                </div>
                <Controller
                    control={control}
                    name='prioritizationMode'
                    render={({ field }) => (
                        <div className='flex flex-col gap-xs'>
                            {PRIORITIZATION_MODE_OPTIONS.map((option) => (
                                <label
                                    key={option.value}
                                    className='flex items-start gap-sm'
                                    style={{ fontSize: '0.9rem' }}
                                >
                                    <input
                                        type='radio'
                                        name={field.name}
                                        value={option.value}
                                        checked={field.value === option.value}
                                        onChange={() => field.onChange(option.value)}
                                        onBlur={field.onBlur}
                                    />
                                    <span>
                                        {option.label}
                                        <span
                                            style={{ display: 'block', fontSize: '0.8rem', color: 'var(--gray-700)' }}
                                        >
                                            {option.description}
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                />

                {dayBoundaryLocked ? (
                    <Tooltip content={TIMEZONE_REQUIREMENT_MESSAGE} position='top'>
                        {dayBoundaryControls}
                    </Tooltip>
                ) : (
                    dayBoundaryControls
                )}

                <div className='mt-md' style={{ maxWidth: '260px' }}>
                    <Input
                        id='queue-delay-alert-threshold'
                        label='Queue delay alert threshold (%)'
                        type='number'
                        placeholder={`Default ${ALERT_THRESHOLD_FALLBACK}`}
                        {...register('queueDelayAlertThreshold', { validate: validateAlertThreshold })}
                        min={ALERT_THRESHOLD_MIN}
                        max={ALERT_THRESHOLD_MAX}
                        error={!!errors.queueDelayAlertThreshold}
                        errorText={errors.queueDelayAlertThreshold?.message}
                    />
                </div>

                <div className='mt-md flex items-center gap-sm'>
                    <Controller
                        control={control}
                        name='retryTimeShiftEnabled'
                        render={({ field }) => (
                            <Toggle
                                // See the same-day toggle above: null is "never
                                // configured", and is not a stored false.
                                checked={field.value ?? false}
                                onChange={(e) => field.onChange(e.target.checked)}
                                onBlur={field.onBlur}
                                name={field.name}
                                aria-label='Toggle retry time shift'
                            />
                        )}
                    />
                    <span className='text-sm text-gray-700'>Retry time shift</span>
                    <Tooltip
                        content='After a voicemail or an unable-to-contact outcome, the next attempt moves to the next time-of-day window: morning to afternoon to evening, then the following morning.'
                        position='top'
                    >
                        <span className='flex items-center'>
                            <InfoIcon size={14} color='var(--primary-purple)' />
                        </span>
                    </Tooltip>
                </div>
            </div>

            <div className='mt-lg' style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', fontWeight: 500 }}>
                    SMS pacing
                </label>
                <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: 'var(--gray-700)' }}>
                    Caps how fast this workflow sends outbound texts. Leave blank to use the default of{' '}
                    {SMS_PACING_DEFAULT} per minute.
                </p>
                <div className='flex items-end gap-sm'>
                    <div style={{ flex: 1, maxWidth: '260px' }}>
                        <Input
                            id='sms-pacing-max-per-minute'
                            label='Max texts per minute'
                            type='number'
                            placeholder={`Default ${SMS_PACING_DEFAULT}`}
                            {...register('smsPacingMaxPerMinute', { validate: validateSmsPacingMaxPerMinute })}
                            min={SMS_PACING_MIN}
                            max={SMS_PACING_MAX}
                            error={!!errors.smsPacingMaxPerMinute}
                            errorText={errors.smsPacingMaxPerMinute?.message}
                        />
                    </div>
                    {watch('smsPacingMaxPerMinute')?.toString().trim() !== '' && (
                        <Button
                            type='button'
                            variant='secondary'
                            onClick={() => {
                                setValue('smsPacingMaxPerMinute', '', { shouldValidate: true, shouldDirty: true });
                            }}
                        >
                            Reset to default
                        </Button>
                    )}
                </div>
            </div>

            {canEditNumberLimits && (
                <div className='mt-lg' style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', fontWeight: 500 }}>
                        Phone lines
                    </label>
                    <div className='flex items-center gap-sm'>
                        <Controller
                            control={control}
                            name='numberLimitsSkipWarmup'
                            render={({ field }) => (
                                <Toggle
                                    checked={field.value ?? false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                    aria-label='Toggle skip number warm-up'
                                />
                            )}
                        />
                        <span className='text-sm text-gray-700'>Skip number warm-up</span>
                    </div>
                    <p style={{ margin: '4px 0 10px', fontSize: '0.8rem', color: 'var(--gray-700)' }}>
                        Every number this workflow dials from becomes usable right away, at the full daily cap.
                    </p>
                    <div style={{ maxWidth: '260px' }}>
                        <Input
                            id='daily-calls-per-number'
                            label='Daily calls per number'
                            type='number'
                            placeholder={`Default ${DAILY_CALL_CAP_DEFAULT}`}
                            {...register('numberLimitsDailyCallCap', { validate: validateDailyCallCap })}
                            min={DAILY_CALL_CAP_MIN}
                            max={DAILY_CALL_CAP_MAX}
                            error={!!errors.numberLimitsDailyCallCap}
                            errorText={errors.numberLimitsDailyCallCap?.message}
                        />
                    </div>
                </div>
            )}

            <PartyStatusModal
                isOpen={isPartyStatusModalOpen}
                onClose={() => setIsPartyStatusModalOpen(false)}
                transitionsOnly={!canEditStatusModel}
            />

            <GenericTagsModal
                isOpen={isGenericModalOpen}
                onClose={() => {
                    if (!getValues('isGenericConfigured')) {
                        handleResetGeneric();
                    }
                    setIsGenericModalOpen(false);
                }}
            />
        </div>
    );
};
